import type { PayrollRun } from "features/Payroll/types/payroll.types";
import { cancelPayrollRunWithFeedback } from "features/Payroll/utils/cancelPayrollRunAction";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";

type CancelPayrollRunConfirmModalProps = {
  run: PayrollRun | null;
  isOpen: boolean;
  onClose: () => void;
  onCancelled?: () => void | Promise<void>;
};

const CancelPayrollRunConfirmModal = ({
  run,
  isOpen,
  onClose,
  onCancelled,
}: CancelPayrollRunConfirmModalProps) => {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  const handleClose = () => {
    if (busy) return;
    setReason("");
    setTouched(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!run) return;
    setTouched(true);
    if (!reason.trim()) return;
    setBusy(true);
    try {
      const { ok, message } = await cancelPayrollRunWithFeedback(run.id, reason);
      if (ok) {
        setReason("");
        setTouched(false);
        onClose();
        toast.success(message);
        await onCancelled?.();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal centered isOpen={isOpen && run != null} toggle={handleClose}>
      <ModalHeader toggle={handleClose}>Cancel payroll run</ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          You are about to cancel payroll run{" "}
          <span className="fw-semibold">{run?.code ?? `#${run?.id}`}</span>. Non-paid payroll items
          in this run will be marked as cancelled.
        </p>
        <div className="mb-0">
          <Label for="cancel-payroll-run-reason" className="form-label">
            Reason <span className="text-danger">*</span>
          </Label>
          <Input
            id="cancel-payroll-run-reason"
            type="textarea"
            rows={3}
            maxLength={500}
            placeholder="Why is this run being cancelled?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={busy}
            invalid={touched && !reason.trim()}
          />
          {touched && !reason.trim() ? (
            <div className="invalid-feedback">Reason is required.</div>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={handleClose} disabled={busy}>
          Keep run
        </Button>
        <Button color="danger" onClick={() => void handleConfirm()} disabled={busy}>
          {busy ? "Cancelling…" : "Cancel run"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CancelPayrollRunConfirmModal;
